import React, { useState, useEffect, useRef, useCallback } from 'react';
import MaterialIcon from '../ui/MaterialIcon';
import { searchAirports } from '../../services/airportSearch';
import { AIRPORTS_BY_IATA } from '../../data/airports';
import { METRO_AREAS } from '../../data/metroAreas';

const POPULAR_IATA = [
  'DXB', 'LHR', 'JFK', 'LAX', 'CDG', 'SIN', 'NRT', 'HND',
  'ICN', 'BKK', 'IST', 'AMS', 'FRA', 'SYD', 'HKG',
];

const POPULAR_AIRPORTS = POPULAR_IATA
  .map((code) => AIRPORTS_BY_IATA.get(code))
  .filter(Boolean);

function AirportSearchModal({ title, isOpen, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      // Focus input after animation frame
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  const [metroResults, setMetroResults] = useState([]);

  const handleSearch = useCallback((value) => {
    setQuery(value);
    if (value.trim()) {
      setResults(searchAirports(value));
      const q = value.toLowerCase();
      setMetroResults(
        METRO_AREAS.filter(
          (m) => m.code.toLowerCase().includes(q) || m.label.toLowerCase().includes(q)
        )
      );
    } else {
      setResults([]);
      setMetroResults([]);
    }
  }, []);

  const handleSelect = useCallback((airport) => {
    onSelect(airport);
    onClose();
  }, [onSelect, onClose]);

  const handleMetroSelect = useCallback((metro) => {
    onSelect({ type: 'metro', code: metro.code, label: metro.label, airports: metro.airports, city: metro.label.replace(' (All)', '') });
    onClose();
  }, [onSelect, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const displayList = query.trim() ? results : POPULAR_AIRPORTS;
  const showEmpty = query.trim() && results.length === 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-50 flex flex-col bg-white transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-surface-container flex items-center justify-center hover:bg-surface-container-high transition-colors"
          >
            <MaterialIcon name="close" size={20} className="text-on-surface" />
          </button>
          <h2 className="text-lg font-bold text-on-surface">{title}</h2>
        </div>

        {/* Search Input */}
        <div className="px-4 pb-3">
          <div className="relative">
            <MaterialIcon
              name="search"
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by city, airport, or code..."
              className="w-full pl-10 pr-10 py-3 bg-surface-container-low rounded-2xl text-base text-on-surface placeholder:text-on-surface-variant/60 outline-none border border-transparent focus:border-primary/40 transition-colors"
            />
            {query && (
              <button
                onClick={() => handleSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <MaterialIcon name="cancel" size={20} className="text-on-surface-variant" />
              </button>
            )}
          </div>
        </div>

        {/* Section Label */}
        {!query.trim() && (
          <div className="px-4 pb-2">
            <p className="text-[11px] font-semibold text-on-surface-variant/70 uppercase tracking-label">
              Popular Airports
            </p>
          </div>
        )}

        {/* Results List */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {showEmpty ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MaterialIcon name="flight" size={48} className="text-outline-variant mb-3" />
              <p className="text-base font-semibold text-on-surface">No airports found</p>
              <p className="text-sm text-on-surface-variant mt-1">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Metro area results */}
              {metroResults.map((metro) => (
                <button
                  key={metro.code}
                  onClick={() => handleMetroSelect(metro)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface-container-low active:bg-primary-light/30 transition-colors text-left"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20">
                    <span className="text-sm font-bold text-primary">{metro.code}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-on-surface truncate">
                      {metro.label}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] font-semibold text-primary bg-primary-light/50 px-1.5 py-0.5 rounded-full">
                        {metro.airports.length} airports
                      </span>
                      <span className="text-xs text-on-surface-variant truncate">
                        {metro.airports.join(', ')}
                      </span>
                    </div>
                  </div>
                  <MaterialIcon name="chevron_right" size={20} className="text-outline-variant flex-shrink-0" />
                </button>
              ))}

              {/* Individual airport results */}
              {displayList.map((airport) => (
                <button
                  key={airport.iata}
                  onClick={() => handleSelect(airport)}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-surface-container-low active:bg-primary-light/30 transition-colors text-left"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary-light/30 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">{airport.iata}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-on-surface truncate">
                      {airport.city}
                    </p>
                    <p className="text-xs text-on-surface-variant truncate">
                      {airport.name} · {airport.country}
                    </p>
                  </div>
                  <MaterialIcon name="chevron_right" size={20} className="text-outline-variant flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AirportSearchModal;
